/**
 * Helpers for OverflowList (label normalization and tooltip line wrapping).
 */

/** At this count or below, each remaining label is its own tooltip line. */
const ONE_PER_LINE_LIMIT = 10;

/** Average label length at or below which lines group three items. */
const SHORT_LABEL_AVG = 18;

/** Joined line length that should stay under so the tooltip does not stretch. */
const MAX_LINE_LENGTH = 72;

/** A single label at or above this length is kept on its own line. */
const LONG_LABEL_LENGTH = 40;

export function toOverflowLabel(item) {
  if (item == null) return '';

  if (typeof item === 'object') {
    const label = item.name || item.displayname || item.email || item.contactemail || item.label;
    return String(label || '').trim();
  }

  return String(item).trim();
}

/**
 * Drop blanks. Keeps original strings or objects so callers can render avatars.
 * @param {Array} items
 * @returns {Array}
 */
export function filterOverflowItems(items) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => toOverflowLabel(item));
}

/**
 * Trim list values to display strings and drop blanks.
 * Strings are preferred; an object may provide `name`, `displayname`, or `email`.
 * @param {Array} items
 * @returns {string[]}
 */
export function normalizeOverflowItems(items) {
  return filterOverflowItems(items).map((item) => toOverflowLabel(item));
}

function averageLabelLength(labels) {
  const total = labels.reduce((sum, label) => sum + label.length, 0);
  return total / labels.length;
}

/**
 * Format overflow labels into tooltip lines.
 * Pass only the labels that are not shown inline.
 * Ten or fewer labels: one per line. More than ten: comma-separated groups of
 * two or three, with long labels kept on their own line.
 * @param {Array} items
 * @returns {string[]}
 */
export function formatOverflowTooltipLines(items) {
  const labels = normalizeOverflowItems(items);

  if (labels.length <= ONE_PER_LINE_LIMIT) {
    return labels;
  }

  const perLine = averageLabelLength(labels) <= SHORT_LABEL_AVG ? 3 : 2;
  const lines = [];
  let index = 0;

  while (index < labels.length) {
    const group = [];

    while (group.length < perLine && index < labels.length) {
      const label = labels[index];

      if (label.length >= LONG_LABEL_LENGTH) {
        if (group.length === 0) {
          group.push(label);
          index += 1;
        }
        break;
      }

      const joined = group.length === 0 ? label : `${group.join(', ')}, ${label}`;
      if (joined.length > MAX_LINE_LENGTH && group.length > 0) {
        break;
      }

      group.push(label);
      index += 1;
    }

    if (group.length) {
      lines.push(group.join(', '));
    }
  }

  return lines;
}
